import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";

import Breadcrumb from "components/Breadcrumb";
import Button from "components/Button";
import {
  ContentArea as BaseContentArea,
  FooterArea as BaseFooterArea,
  Board as BaseBoard,
} from "components/LayoutParts";
import UploadForm from "components/UploadForm";
import UserModal, { UserModalConfig } from "components/UserModal";
import color from "constants/color";
import font from "constants/font";
import { ERROR, SUCCESS } from "pages/SyncPort/Company/features/utils/modalTemplate";
import {
  useFindPropertyById,
  useFindSpaceGroupById,
  useUpdateSyncPortImage,
} from "pages/SyncPort/Company/Property/features/api";

const Board = styled(BaseBoard)`
  overflow-y: clip;
  padding: 0;
  position: relative;
  border: none;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-between;
`;
const PropertyTitleArea = styled.div`
  display: flex;
  flex-direction: row;
  margin: 0 0 10px 0;
  align-items: center;
  width: 100%;
`;
const ContentArea = styled(BaseContentArea)`
  display: flex;
  flex-direction: row;
  margin-top: 16px;
  padding: 32px 32px 22px 32px;
  gap: 10px;
  background-color: ${color.white};
`;
const FooterArea = styled(BaseFooterArea)`
  bottom: 0;
`;
const CancelWrap = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
`;
const SaveWrap = styled(CancelWrap)`
  align-items: center;
  justify-content: flex-end;
`;
const Annotation = styled.span`
  font-size: ${font.size12};
  color: ${color.text.gray};
  margin-right: 16px;
  gap: 4px;
  display: flex;
  align-items: center;
`;

// パーツ定義
const UploadParkingGuide = () => {
  const navigate = useNavigate();
  const { id: companyId, propertyId, spaceGroupId } = useParams();
  const { data: property } = useFindPropertyById({ propertyId });
  const { data: spaceGroup, mutate } = useFindSpaceGroupById({ spaceGroupId });
  const { updateImages } = useUpdateSyncPortImage("spaceGroup");

  const [newImages, setNewImages] = useState<File[]>([]);
  const [parkingGuides, setParkingGuides] = useState<Image[]>(spaceGroup?.images ?? []);
  const [isVisible, setIsVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState<UserModalConfig>({
    onClickOutside: () => {
      return;
    },
    subtitle: "変更しました",
    main: {
      buttonType: "secondary",
      onClick: () => {
        return;
      },
      label: "閉じる",
    },
  });

  const imageTotalLength = parkingGuides.length + newImages.length;
  const imageLengthSame = imageTotalLength === spaceGroup?.images.length;
  const imageSame = JSON.stringify(spaceGroup?.images) === JSON.stringify(parkingGuides);
  const submitDisabled = imageLengthSame && imageSame;

  const showModal = (modalConfig?: UserModalConfig) => {
    modalConfig != null ? setModalConfig(modalConfig) : null;
    setIsVisible(true);
  };

  const hideModal = () => {
    setIsVisible(false);
  };

  const handleSubmit = async () => {
    const res = await updateImages({
      id: spaceGroupId ?? "",
      currentImageUrls: parkingGuides.map((image) => image.url),
      newImages,
    }).catch(() => {
      showModal(ERROR(() => hideModal()));
      return null;
    });
    if (res) {
      showModal(
        SUCCESS(async () => {
          await mutate();
          hideModal();
          navigate(`/sync_port/company/${companyId}/property/${propertyId}/${spaceGroupId}`);
        })
      );
    }
  };

  const setUp = useCallback(() => {
    newImages.length === 0 && setParkingGuides(spaceGroup?.images ?? []);
  }, [spaceGroup, newImages]);

  useEffect(() => {
    setUp();
  }, [setUp]);

  return (
    <Board>
      <div>
        <PropertyTitleArea>
          <Breadcrumb
            currentPageName="区画図を登録"
            breadcrumbItems={[
              {
                pageName: property?.name ? property?.name : "駐車場名",
                onClick: () => navigate(`/sync_port/company/${companyId}/property/${propertyId}`),
              },
              {
                pageName: "区画グループ",
                onClick: () => navigate(`/sync_port/company/${companyId}/property/${propertyId}/${spaceGroupId}`),
              },
            ]}
          />
        </PropertyTitleArea>
        <ContentArea>
          <UploadForm
            showImages={parkingGuides}
            setShowImages={setParkingGuides}
            unUploadedImages={newImages}
            setUnUploadedImages={setNewImages}
            twoRows
          ></UploadForm>
        </ContentArea>
      </div>
      <FooterArea>
        <CancelWrap>
          <Button
            type="secondary"
            onClick={() => navigate(`/sync_port/company/${companyId}/property/${propertyId}/${spaceGroupId}`)}
            label="キャンセル"
            width="160px"
          />
        </CancelWrap>
        <SaveWrap>
          <Annotation>保存するまで変更内容は反映されません</Annotation>
          <Button
            type="primary"
            onClick={handleSubmit}
            label="変更内容を保存"
            width="180px"
            disabled={submitDisabled}
          />
        </SaveWrap>
      </FooterArea>
      <UserModal isVisible={isVisible} config={modalConfig} />
    </Board>
  );
};

export default UploadParkingGuide;
